import { usePrivacyMask } from "@nimit9/signet-ui/components/privacy-mask"
import { createRootRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router"

import { useHotkeys } from "react-hotkeys-hook"
import { CommandPalette } from "@/components/layout/command-palette"
import { GlobalDropZone } from "@/components/layout/global-drop-zone"
import { OnboardingGate } from "@/components/layout/onboarding-gate"
import { PanelRoot } from "@/components/layout/panel"
import { PinGate, UnveiledOnly, VeilInert } from "@/components/layout/pin-gate"
import { QuickAddLauncher } from "@/components/layout/quick-add-composer"
import { ShortcutsHelp, useShortcutsHelp } from "@/components/layout/shortcuts-help"
import { Sidebar } from "@/components/layout/sidebar"
import { TabBar } from "@/components/layout/tab-bar"
import { GoalDetail } from "@/features/goals/goal-detail"
import { TransactionDetail } from "@/features/transactions/transaction-detail"
import { useAlertsSSE } from "@/hooks/use-alerts"
import { useBackfillEncryption } from "@/hooks/use-backfill-encryption"
import { useSessionKeepalive } from "@/hooks/use-session-keepalive"
import { useDataLocked } from "@/hooks/use-veiled"
import { useUIStore } from "@/store/ui"

export const Route = createRootRoute({
  component: RootLayout,
})

function renderPanelContent({ type, data }: { type: string; data: Record<string, unknown> }) {
  switch (type) {
    case "transaction":
      return <TransactionDetail data={data} />
    case "goal":
      return <GoalDetail data={data} />
    default:
      return <p className="text-text-muted">Unknown panel type</p>
  }
}

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (pathname === "/login" || pathname === "/join" || pathname === "/zerodha-return") {
    return <Outlet />
  }
  return <AppLayout />
}

export function AppLayout() {
  const navigate = useNavigate()
  const closePanel = useUIStore((s) => s.closePanel)
  const openCommandPalette = useUIStore((s) => s.openCommandPalette)
  const shortcuts = useShortcutsHelp()
  const dataLocked = useDataLocked()
  const { toggle: toggleMask } = usePrivacyMask()

  // No alerts stream while user data is locked (veil up / mode unknown).
  useAlertsSSE({ enabled: !dataLocked })
  useSessionKeepalive()
  useBackfillEncryption()

  useHotkeys("escape", closePanel)
  useHotkeys("mod+k", openCommandPalette, { preventDefault: true })
  useHotkeys("g,d", () => navigate({ to: "/" }))
  useHotkeys("g,t", () => navigate({ to: "/transactions" }))
  useHotkeys("g,a", () => navigate({ to: "/analytics" }))
  useHotkeys("g,b", () => navigate({ to: "/budgets" }))
  useHotkeys("g,g", () => navigate({ to: "/goals" }))
  useHotkeys("g,n", () => navigate({ to: "/net-worth" }))
  useHotkeys("g,i", () => navigate({ to: "/investments" }))
  useHotkeys("g,r", () => navigate({ to: "/reports" }))
  useHotkeys("g,s", () => navigate({ to: "/settings" }))
  useHotkeys("g,u", () => navigate({ to: "/upcoming" }))
  useHotkeys("g,c", () => navigate({ to: "/calculators" }))
  // PAISA-PRIV-01: `h` toggles the "Hide amounts" mask (ignored while typing).
  useHotkeys("h", () => toggleMask())

  return (
    <div className="flex h-screen overflow-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-[13px] focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:ring-2 focus:ring-ring/50"
      >
        Skip to main content
      </a>
      <OnboardingGate>
        {/* Private mode, locked: the shell stays visible but inert behind the
            veil, and overlays that can fetch or show rows aren't mounted. */}
        <VeilInert>
          <Sidebar />
        </VeilInert>
        <div className="flex flex-1 flex-col overflow-hidden">
          <PinGate>
            <Outlet />
          </PinGate>
        </div>
        <UnveiledOnly>
          <PanelRoot renderContent={renderPanelContent} />
          <CommandPalette />
          <GlobalDropZone />
          {/* Desktop `Q` quick-add composer (never while veiled). */}
          <QuickAddLauncher />
        </UnveiledOnly>
        <ShortcutsHelp open={shortcuts.open} onClose={shortcuts.close} />
        <VeilInert>
          <TabBar />
        </VeilInert>
      </OnboardingGate>
    </div>
  )
}
